"use client";

import {
	faChevronLeft,
	faChevronRight,
	faCloudPlus,
	faFile,
	faFilePlus,
	faTrashAlt,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ErrorMessage } from "@hookform/error-message";
import { type ChangeEvent, Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { match } from "ts-pattern";
import { FormErrorMessage, cn, normalizeFiles } from "../../..";

export interface FileInputWithVisualProps {
	lang: string;
	name: string;
	messages: {
		label: string;
		button: string;
		mainFile?: string;
	};
	errorMessages: {
		general: string;
		file_too_small: string;
		file_too_big: string;
		invalid_file_type: string;
		too_few_files: string;
		too_many_files: string;
	};
	minFileSize: number;
	maxFileSize: number;
	minAmountFiles: number;
	maxAmountFiles: number;
	acceptedFileTypes: { extension: string; mimeType: string }[];
}

export function FileInputWithVisual(props: FileInputWithVisualProps) {
	const isSafari =
		typeof window !== "undefined"
			? /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent)
			: false;
	const accept = new Intl.ListFormat(props.lang, { type: "conjunction" }).format(
		props.acceptedFileTypes.map(({ extension }) => extension),
	);
	const form = useFormContext();
	const fieldState = form.getFieldState(props.name);
	const invalid = fieldState?.invalid;
	const watchedInput: unknown = form.watch(props.name) ?? undefined;
	const formFiles = normalizeFiles(watchedInput);
	const formFilesItems = formFiles
		.map((file, index) => ({ file, order: index }))
		.sort((a, b) => a.order - b.order);

	const [items, setItems] = useState<typeof formFilesItems>(formFilesItems ?? []);
	const [dragOrigin, setDragOrigin] = useState<number | null>(null);
	const [dragTarget, setDragTarget] = useState<number | null>(null);
	const [drop, setDrop] = useState(false);
	const [errors, setErrors] = useState<Record<string, string | undefined>>();

	const convertBtoMB = useCallback((bytes: number) => bytes / 1000000, []);
	const convertBtoKB = useCallback((bytes: number) => bytes / 1000, []);

	const isValidFileType = useCallback(
		(file: File) => {
			return file.type
				? !!props.acceptedFileTypes.find((type) => type.mimeType === file.type)
				: props.acceptedFileTypes.find((type) => file.name.endsWith(type.extension));
		},
		[props.acceptedFileTypes],
	);

	const validateFile = useCallback(
		(file: File) => {
			if (!isValidFileType(file)) {
				return "invalid_file_type";
			}
			if (file.size < props.minFileSize) {
				return "file_too_small";
			}
			if (file.size > props.maxFileSize) {
				return "file_too_big";
			}
		},
		[isValidFileType, props.maxFileSize, props.minFileSize],
	);

	const validateFiles = useCallback(
		(files: File[]) => {
			if (files.length < props.minAmountFiles) {
				return "too_few_files";
			}
			if (files.length > props.maxAmountFiles) {
				return "too_many_files";
			}
		},
		[props.maxAmountFiles, props.minAmountFiles],
	);

	const errorMessage = useMemo(
		() => (error: string) =>
			match(error)
				.with(
					"file_too_small",
					() => `${props.errorMessages.file_too_small} ${convertBtoKB(props.minFileSize)}kb`,
				)
				.with(
					"file_too_big",
					() => `${props.errorMessages.file_too_big} ${convertBtoMB(props.maxFileSize)}mb`,
				)
				.with("invalid_file_type", () => `${props.errorMessages.invalid_file_type} ${accept}`)
				.with("too_few_files", () => `${props.errorMessages.too_few_files} ${props.minAmountFiles}`)
				.with(
					"too_many_files",
					() => `${props.errorMessages.too_many_files} ${props.maxAmountFiles}`,
				)
				.otherwise(() => props.errorMessages.general),
		[
			accept,
			props.errorMessages.file_too_big,
			props.errorMessages.file_too_small,
			props.errorMessages.general,
			props.errorMessages.invalid_file_type,
			props.errorMessages.too_few_files,
			props.errorMessages.too_many_files,
			convertBtoKB,
			convertBtoMB,
			props.maxFileSize,
			props.minFileSize,
			props.maxAmountFiles,
			props.minAmountFiles,
		],
	);

	const validate = useCallback(
		(files: File[]) => {
			const errors = files.reduce(
				(acc, file) => Object.assign(acc, { [file.name]: validateFile(file) }),
				{} as Record<string, string | undefined>,
			);

			setErrors(errors);

			const hasFileErrors = errors ? Object.values(errors).some((error) => !!error) : false;
			const hasGeneralError = validateFiles(files);

			if (hasGeneralError) {
				form.setError(props.name, { type: "manual", message: errorMessage(hasGeneralError) });
			} else if (hasFileErrors) {
				form.setError(props.name, { type: "manual", message: errorMessage("general") });
			} else {
				form.clearErrors(props.name);
			}

			form.setValue(props.name, files);

			setItems(files.map((file, order) => ({ file, order })).sort((a, b) => a.order - b.order));
		},
		[
			form.setValue,
			form.setError,
			form.clearErrors,
			props.name,
			errorMessage,
			validateFile,
			validateFiles,
		],
	);

	const onAddFiles = useCallback(
		(fileList: FileList | null) => {
			if (fileList instanceof FileList) {
				const newFiles = Array.from(fileList);
				const combinedFiles = [...(formFiles ?? []), ...newFiles];
				const uniqueFiles = combinedFiles.filter(
					(obj, index) => combinedFiles.findIndex((item) => item.name === obj.name) === index,
				);

				validate(uniqueFiles);
			}
		},
		[formFiles, validate],
	);

	const handleDragEnd = () => {
		if (dragTarget !== null && dragOrigin !== null) {
			const newItems = [...items];
			const draggingItem = newItems[dragOrigin];
			if (!draggingItem) return;

			newItems.splice(dragOrigin, 1);
			newItems.splice(dragTarget, 0, draggingItem);

			const reorderedItems = newItems.map((file, index) => ({ ...file, order: index }));

			setItems(reorderedItems);
			setDragOrigin(null);
			setDragTarget(null);
		}
	};

	const swapItemOrder = (index: number, swapIndex: number) => {
		const newItems = [...items];
		const current = newItems[index];
		const swapItem = newItems[swapIndex];

		if (swapItem !== undefined && current !== undefined) {
			newItems[index] = swapItem;
			newItems[swapIndex] = current;
		}

		setItems(newItems);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: only want to run this effect when the form is submitted
	useEffect(() => {
		if (fieldState.isDirty) {
			validate(formFiles);
		}
	}, [form.formState.submitCount]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: attach event listeners only once
	useEffect(() => {
		const handleDrop = (e: DragEvent) => {
			if (!dragOrigin) {
				e.preventDefault();
				onAddFiles(e.dataTransfer?.files ?? null);
				setDrop(false);
			}
		};

		const handleDragOver = (e: DragEvent) => {
			if (!dragOrigin) {
				e.preventDefault();
				setDrop(true);
			}
		};

		const handleDragLeave = (e: DragEvent) => {
			if (!dragOrigin) {
				e.preventDefault();
				setDrop(false);
			}
		};

		document.body.addEventListener("drop", handleDrop);
		document.body.addEventListener("dragover", handleDragOver);
		document.body.addEventListener("dragleave", handleDragLeave);

		return () => {
			document.body.removeEventListener("drop", handleDrop);
			document.body.removeEventListener("dragover", handleDragOver);
			document.body.removeEventListener("dragleave", handleDragLeave);
		};
	}, []);

	return (
		<div className="flex flex-col gap-2">
			<label
				className={cn(
					"group flex w-full cursor-pointer flex-wrap items-center justify-between gap-x-6 gap-y-4 rounded border enter:border-primary-medium border-dashed enter:bg-primary-light/50 px-6 py-4 outline-none motion-safe:transition-colors",
					{ "border-primary": !drop, "border-primary-medium bg-primary-light/50": drop },
				)}
				onDrop={(e) => {
					e.preventDefault();
					onAddFiles(e.dataTransfer.files);
					setDrop(false);
				}}
				onDragOver={(e) => {
					e.preventDefault();
					setDrop(true);
				}}
				onDragLeave={(e) => {
					e.preventDefault();
					setDrop(false);
				}}
			>
				<input
					className="hidden"
					type="file"
					multiple
					accept={accept}
					{...form.register(props.name, {
						onChange: (e: ChangeEvent<HTMLInputElement>) => onAddFiles(e.target.files),
						shouldUnregister: true,
					})}
				/>

				<span className="flex items-center gap-4">
					<FontAwesomeIcon icon={faCloudPlus} className="text-2xl text-primary" />
					<span className="text-shades-dark">{props.messages.label}</span>
				</span>

				<div
					className={cn(
						"inline-block rounded border-2 bg-white px-4 py-2 text-center font-bold font-primary text-sm group-enter:border-primary-medium group-enter:text-primary-medium motion-safe:transition-colors",
						{
							"border-primary text-primary": !drop,
							"border-primary-medium text-primary-medium": drop,
						},
					)}
				>
					{props.messages.button}
				</div>
			</label>

			{invalid && (
				<div className="text-danger text-sm">
					<ErrorMessage name={props.name} />
				</div>
			)}

			{!!items?.length && (
				<output className="grid grid-cols-2 xs:grid-cols-3 gap-4 sm:grid-cols-4 lg:grid-cols-5">
					{items.map((item, index) => {
						const error = errors?.[item.file.name];
						const showImage =
							item.file.type.includes("image") && (!item.file.type.includes("heic") || isSafari);

						return (
							<Fragment key={item.file.name}>
								{!!dragOrigin && index !== dragOrigin && index === dragTarget && (
									<div
										onDragOver={(e) => {
											e.preventDefault();
											e.dataTransfer.dropEffect = "move";
										}}
										className="flex aspect-square w-full items-center justify-center rounded border border-primary border-dashed bg-white text-5xl text-primary"
									>
										<FontAwesomeIcon icon={faFilePlus} fixedWidth />
									</div>
								)}

								<div
									draggable
									onDragStart={() => setDragOrigin(index)}
									onDragOver={(e) => {
										e.preventDefault();
										e.dataTransfer.dropEffect = "move";
										setDragTarget(index);
									}}
									onDragEnter={() => setDragTarget(index)}
									onDragEnd={() => handleDragEnd()}
									onDrop={() => {
										setDragOrigin(null);
										setDragTarget(null);
									}}
									className={cn(
										"group relative select-none rounded bg-shades-light active:cursor-grabbing",
										{
											"cursor-grab": !dragOrigin,
											"cursor-grabbing": dragOrigin,
											"opacity-40": dragOrigin === index,
										},
									)}
								>
									<div
										className={cn("relative aspect-square rounded border", {
											"border-danger": !!error,
											"border-shades": !error,
										})}
									>
										{showImage ? (
											<img
												src={URL.createObjectURL(item.file)}
												alt={item.file.name}
												className="pointer-events-none absolute inset-0 h-full w-full object-contain object-center"
											/>
										) : (
											<div className="flex h-full w-full flex-col items-center justify-center text-center">
												<FontAwesomeIcon icon={faFile} className="text-[2rem]" />
												<span className="mt-4 w-full truncate px-2 text-shades-dark text-xs">
													{item.file.name}
												</span>
											</div>
										)}

										{index === 0 && !!props.messages.mainFile && (
											<div className="absolute top-2 left-2 flex items-center justify-center rounded bg-shades-dark px-2 py-1 text-sm text-white leading-none">
												{props.messages.mainFile}
											</div>
										)}

										<button
											type="button"
											className="no-touch:invisible absolute top-2 right-2 flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full bg-danger text-white no-touch:opacity-0 outline-none hover:bg-danger-medium focus:bg-danger-medium group-hover:visible group-hover:opacity-100 motion-safe:transition"
											onClick={() => validate(formFiles.filter((v) => v !== item.file))}
										>
											<FontAwesomeIcon icon={faTrashAlt} fixedWidth size="xs" />
										</button>

										{index + 1 < items.length && (
											<button
												type="button"
												className="no-touch:invisible absolute right-2 bottom-2 flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full bg-primary text-white no-touch:opacity-0 outline-none focus:bg-primary-medium motion-safe:transition"
												onClick={() => swapItemOrder(index, index + 1)}
											>
												<FontAwesomeIcon icon={faChevronRight} fixedWidth size="xs" />
											</button>
										)}

										{index > 0 && (
											<button
												type="button"
												className="no-touch:invisible absolute bottom-2 left-2 flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full bg-primary text-white no-touch:opacity-0 outline-none focus:bg-primary-medium motion-safe:transition"
												onClick={() => swapItemOrder(index, index - 1)}
											>
												<FontAwesomeIcon icon={faChevronLeft} fixedWidth size="xs" />
											</button>
										)}
									</div>

									{!!error && (
										<FormErrorMessage variant="danger" className="text-center text-xs">
											{errorMessage(error)}
										</FormErrorMessage>
									)}
								</div>
							</Fragment>
						);
					})}
				</output>
			)}
		</div>
	);
}
